/**
 * Voice Subscription API Service
 *
 * Calls pricing-svc /api/voice/* endpoints using cookie-based session auth
 * (same pattern as pricingApi.ts — credentials: 'include').
 */

import { apiConfig } from '@/config/apiConfig';
import type {
  VoiceSubscriptionResponse,
  VoiceEntitlements,
  VoiceSuccessFeeSummary,
  VoiceMinutesRemaining,
  VoiceUsageSummary,
  VoiceBillingActionLogResponse,
  VoiceNotChargedSummaryResponse,
  VoiceTrialActivityResponse,
  VoiceChangeTierRequest,
  VoiceActivateRequest,
  VoiceSetVerticalRequest,
  VoiceSetOutcomesTrackRequest,
  VoiceStartTrialRequest,
  VoiceServiceCatalogResponse,
  VoiceServicePurchaseRequest,
  VoiceServicePurchaseResponse,
  VoiceAlertPreferencesRequest,
  VoiceAlertPreferencesResponse,
  VoiceSizingRequest,
  VoiceSizingResponse,
  VoiceTier,
  VoiceVertical,
  VoiceOutcomeBand,
} from '@/types/voice-subscription';

const BASE_URL = apiConfig.baseUrls.pricing;

function errorMessageFromBody(body: any, status: number): string {
  const detail = body?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  if (typeof body?.message === 'string') return body.message;
  return `Request failed (${status})`;
}

async function fetchVoice<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const resp = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ message: 'Request failed' }));
    const message = errorMessageFromBody(body, resp.status);
    // First-time accounts — missing subscription is expected, not a hard failure
    if (resp.status === 404 || /no voice subscription/i.test(message)) {
      return { success: false, message } as T;
    }
    throw new Error(message);
  }

  return resp.json() as Promise<T>;
}

// ── Read ───────────────────────────────────────────────────────────────────

/** Missing subscription is a normal first-time state — never throws for that. */
export async function getVoiceSubscription(tenantId: number): Promise<VoiceSubscriptionResponse> {
  const resp = await fetch(`${BASE_URL}/api/voice/subscription/${tenantId}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (resp.status === 404) {
    return { success: false, message: 'No voice subscription found' };
  }

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ message: 'Request failed' }));
    const message = errorMessageFromBody(body, resp.status);
    // Some deployments return 400/200-with-message instead of 404 for "none yet"
    if (/no voice subscription/i.test(message)) {
      return { success: false, message };
    }
    throw new Error(message);
  }

  return resp.json() as Promise<VoiceSubscriptionResponse>;
}

export async function getVoiceEntitlements(tenantId: number): Promise<VoiceEntitlements> {
  return fetchVoice<VoiceEntitlements>(`/api/voice/subscription/${tenantId}/entitlements`);
}

export async function getVoiceMinutes(tenantId: number): Promise<VoiceMinutesRemaining> {
  return fetchVoice<VoiceMinutesRemaining>(`/api/voice/usage/minutes/${tenantId}`);
}

export async function getVoiceSuccessFeeSummary(
  tenantId: number,
  billingPeriod?: string
): Promise<VoiceSuccessFeeSummary> {
  const qs = billingPeriod ? `?billing_period=${billingPeriod}` : '';
  return fetchVoice<VoiceSuccessFeeSummary>(
    `/api/voice/success-fees/summary/${tenantId}${qs}`
  );
}

export async function getVoiceUsageSummary(
  tenantId: number,
  billingPeriod?: string
): Promise<VoiceUsageSummary> {
  const qs = billingPeriod ? `?billing_period=${billingPeriod}` : '';
  return fetchVoice<VoiceUsageSummary>(`/api/voice/usage/summary/${tenantId}${qs}`);
}

export async function getVoiceBillingActionLog(
  tenantId: number,
  billingPeriod?: string,
  limit = 50,
  offset = 0
): Promise<VoiceBillingActionLogResponse> {
  const params = new URLSearchParams();
  if (billingPeriod) params.set('billing_period', billingPeriod);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return fetchVoice<VoiceBillingActionLogResponse>(
    `/api/voice/billing/action-log/${tenantId}?${params.toString()}`
  );
}

export async function getVoiceNotChargedSummary(
  tenantId: number,
  billingPeriod?: string
): Promise<VoiceNotChargedSummaryResponse> {
  const qs = billingPeriod ? `?billing_period=${billingPeriod}` : '';
  return fetchVoice<VoiceNotChargedSummaryResponse>(
    `/api/voice/billing/not-charged/${tenantId}${qs}`
  );
}

export async function getVoiceTrialActivity(tenantId: number): Promise<VoiceTrialActivityResponse> {
  return fetchVoice<VoiceTrialActivityResponse>(`/api/voice/trial/activity/${tenantId}`);
}

// ── Writes ─────────────────────────────────────────────────────────────────

export async function startVoiceTrial(tenantId: number): Promise<VoiceSubscriptionResponse> {
  return fetchVoice<VoiceSubscriptionResponse>('/api/voice/subscription/trial/start', {
    method: 'POST',
    body: JSON.stringify({ tenant_id: tenantId } satisfies VoiceStartTrialRequest),
  });
}

export async function changeVoiceTier(payload: VoiceChangeTierRequest): Promise<VoiceSubscriptionResponse> {
  return fetchVoice<VoiceSubscriptionResponse>('/api/voice/subscription/change-tier', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** First-time paid-plan selection — see VoiceActivateRequest. */
export async function activateVoiceSubscription(payload: VoiceActivateRequest): Promise<VoiceSubscriptionResponse> {
  return fetchVoice<VoiceSubscriptionResponse>('/api/voice/subscription/activate', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function cancelVoiceSubscription(tenantId: number): Promise<VoiceSubscriptionResponse> {
  return fetchVoice<VoiceSubscriptionResponse>('/api/voice/subscription/cancel', {
    method: 'POST',
    body: JSON.stringify({ tenant_id: tenantId }),
  });
}

export async function reactivateVoiceSubscription(
  tenantId: number,
  newTier: VoiceTier
): Promise<VoiceSubscriptionResponse> {
  return fetchVoice<VoiceSubscriptionResponse>('/api/voice/subscription/reactivate', {
    method: 'POST',
    body: JSON.stringify({ tenant_id: tenantId, new_tier: newTier }),
  });
}

export async function setVoiceVertical(
  tenantId: number,
  vertical: VoiceVertical | null,
  outcomeBand?: VoiceOutcomeBand | null
): Promise<VoiceSubscriptionResponse> {
  return fetchVoice<VoiceSubscriptionResponse>('/api/voice/subscription/vertical', {
    method: 'POST',
    body: JSON.stringify({
      tenant_id: tenantId,
      vertical,
      ...(outcomeBand ? { outcome_band: outcomeBand } : {}),
    } satisfies VoiceSetVerticalRequest),
  });
}

export async function getVoiceServiceCatalog(tenantId: number): Promise<VoiceServiceCatalogResponse> {
  return fetchVoice<VoiceServiceCatalogResponse>(`/api/voice/services/${tenantId}`);
}

export async function purchaseVoiceService(
  payload: VoiceServicePurchaseRequest
): Promise<VoiceServicePurchaseResponse> {
  return fetchVoice<VoiceServicePurchaseResponse>('/api/voice/services/purchase', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function setVoiceOutcomesTrack(
  tenantId: number,
  enabled: boolean
): Promise<VoiceSubscriptionResponse> {
  return fetchVoice<VoiceSubscriptionResponse>('/api/voice/subscription/outcomes-track', {
    method: 'POST',
    body: JSON.stringify({ tenant_id: tenantId, enabled } satisfies VoiceSetOutcomesTrackRequest),
  });
}

export async function getVoiceAlertPreferences(tenantId: number): Promise<VoiceAlertPreferencesResponse> {
  return fetchVoice<VoiceAlertPreferencesResponse>(`/api/voice/alert-preferences/${tenantId}`);
}

export async function saveVoiceAlertPreferences(
  payload: VoiceAlertPreferencesRequest
): Promise<VoiceAlertPreferencesResponse> {
  return fetchVoice<VoiceAlertPreferencesResponse>('/api/voice/alert-preferences', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function getVoiceSizing(tenantId: number): Promise<VoiceSizingResponse> {
  return fetchVoice<VoiceSizingResponse>(`/api/voice/sizing/${tenantId}`);
}

export async function putVoiceSizing(
  tenantId: number,
  payload: VoiceSizingRequest
): Promise<VoiceSizingResponse> {
  return fetchVoice<VoiceSizingResponse>(`/api/voice/sizing/${tenantId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}
